import cx from 'classnames';
import { isBefore } from 'date-fns';
import type { OrderOrder } from '../../../types/codexOrder';
import {
  OrderStatus,
  type OrderStatusType,
} from '../../../constants/OrderStatus';
import {
  PanIcon,
  CashIcon,
  BucketIcon,
  InvoiceIcon,
  SuccessIcon,
  CancelOrderIcon,
  PanDelayIcon,
} from '../../../assets/Icons';
import { capitalize, formatDate } from '../../../helpers/utils';
import { ApiConstants } from '../../../constants/ApiConstants';

interface StatusInfo {
  icon: React.ReactElement;
  progress?: [number, number, number];
  primaryText: string | React.ReactElement;
  secondaryText?: string | React.ReactElement | null;
}

function OrderStatusInfo({
  order,
  secondaryColor,
}: {
  order: OrderOrder;
  secondaryColor: string | undefined;
}) {
  const deliveryTime = order?.details?.delivery_datetime;
  const status: OrderStatusType = order?.details?.order_state;
  const isOrderDelayed = deliveryTime
    ? isBefore(deliveryTime, Date.now())
    : false;
  const orderType = capitalize(order?.details?.order_type);
  const deliveryTimeFormatted = deliveryTime
    ? formatDate(deliveryTime, 'hh:mm a')
    : '';
  const progressClass =
    secondaryColor === '#ffffff'
      ? 'bg-up-black-80 opacity-50'
      : 'bg-up-black-5';
  const isDelivery = order?.details?.order_type === ApiConstants.TYPE_DELIVERY;

  const statusInfo: Partial<Record<OrderStatusType, StatusInfo>> = {
    [OrderStatus.AWAITING_CHECKOUT]: {
      primaryText: 'One last step! Complete the checkout to place your order.',
      icon: <CashIcon className="w-7 h-7" />,
    },
    [OrderStatus.AWAITING_PAYMENT]: {
      primaryText: 'Please complete your payment to get your order started!',
      icon: <CashIcon className="w-7 h-7" />,
    },
    [OrderStatus.EXPIRED]: {
      primaryText:
        "Order expired - payment wasn't completed. You can place a new one anytime!",
      icon: <CancelOrderIcon />,
    },
    [OrderStatus.PLACED]: {
      primaryText: (
        <>
          <p>We're getting started!</p>
          <p>
            Your order will be {isDelivery ? 'delivered' : 'ready'} by{' '}
            {deliveryTimeFormatted}
          </p>
        </>
      ),
      icon: <InvoiceIcon className="w-7 h-7" />,
      progress: [1, 0, 0],
    },
    [OrderStatus.ACKNOWLEDGED]: {
      primaryText: (
        <>
          {isOrderDelayed ? (
            <>
              <p>Your order is delayed!</p>
              <p>We'll let you know as soon as it's ready.</p>
            </>
          ) : (
            <p>We're preparing your order, hang tight!</p>
          )}
        </>
      ),
      secondaryText: isOrderDelayed
        ? `${orderType} Delayed`
        : `${orderType} by ${deliveryTimeFormatted}`,
      icon: isOrderDelayed ? <PanDelayIcon /> : <PanIcon />,
      progress: [1, 1, 0],
    },
    [OrderStatus.FOOD_READY]: {
      primaryText: isDelivery ? (
        <p>On the move! Your order is heading your way now!</p>
      ) : (
        <>
          <p>Your order is ready!</p>
          <p>Please head over to pick it up.</p>
        </>
      ),
      secondaryText: isDelivery
        ? `Arriving by ${deliveryTimeFormatted}`
        : 'Pickup Now!',
      icon: <BucketIcon />,
      progress: [1, 1, 1],
    },
    [OrderStatus.DISPATCHED]: {
      primaryText: isDelivery ? (
        <p>On the move! Your order is heading your way now!</p>
      ) : (
        <>
          <p>Your order is ready!</p>
          <p>Please head over to pick it up.</p>
        </>
      ),
      secondaryText: isDelivery
        ? `Arriving by ${deliveryTimeFormatted}`
        : 'Pickup Now!',
      icon: <BucketIcon />,
      progress: [1, 1, 1],
    },
    [OrderStatus.COMPLETED]: {
      primaryText: (
        <>
          <p>Your order is {isDelivery ? 'delivered' : 'complete'}.</p>
          <p>See you next time!</p>
        </>
      ),
      icon: <SuccessIcon className="w-9 h-9" />,
    },
    [OrderStatus.CANCELLED]: {
      primaryText:
        "We're sorry, your order  has been canceled. Your payment will be refunded shortly.",
      icon: <CancelOrderIcon />,
    },
  };

  return (
    <div
      className={cx('p-4 mb-4 rounded-lg bg-brand-background text-brand-text')}>
      <div className="flex justify-between items-start gap-x-2">
        <div className="p-3 rounded-lg bg-up-black-5 text-brand-icon min-w-10 min-h-10 shrink-0">
          {statusInfo[status]?.icon ?? <InvoiceIcon className="w-7 h-7" />}
        </div>
        <p className="text-sm">
          <span className="font-semibold">Order</span>{' '}
          <span className="font-bold">
            #{order?.details?.ext_platforms?.[0]?.id}
          </span>
        </p>
      </div>
      <div className="mt-6 font-medium text-2xl leading-9 pr-6">
        {statusInfo[status]?.primaryText ??
          `Order is in ${status?.toLowerCase()} state`}
      </div>
      {statusInfo[status]?.secondaryText && (
        <p className="font-semibold text-sm mt-6 mb-4">
          {statusInfo[status]?.secondaryText}
        </p>
      )}
      {!!statusInfo[status]?.progress?.length && (
        <div
          className={cx(
            'flex justify-between gap-x-2 items-center w-full',
            statusInfo[status]?.secondaryText ? 'mt-4' : 'mt-6',
          )}>
          <div
            className={cx(
              'h-1.5 rounded-full flex-1',
              statusInfo[status]?.progress?.[0]
                ? 'bg-brand-text'
                : progressClass,
            )}
          />
          <div
            className={cx(
              'h-1.5 rounded-full flex-1',
              statusInfo[status]?.progress?.[1]
                ? 'bg-brand-text'
                : progressClass,
            )}
          />
          <div
            className={cx(
              'h-1.5 rounded-full flex-1',
              statusInfo[status]?.progress?.[2]
                ? 'bg-brand-text'
                : progressClass,
            )}
          />
        </div>
      )}
    </div>
  );
}

export default OrderStatusInfo;
